/**
 * 全局悬浮反馈组件（Bug 收集器）
 * ES module，通过 <script type="module"> 引入即可自动激活。
 * 不依赖任何框架，纯 vanilla JS。
 */
import { showToast } from './toast.js';

// ==================== DOM 构建 ====================
// 悬浮按钮
const trigger = document.createElement('button');
trigger.id = 'feedback-trigger';
trigger.title = '反馈问题';
trigger.textContent = '🐞';
trigger.setAttribute('aria-label', '提交反馈');

function createFeedbackElement(tagName, { id = '', className = '', text = '', attrs = {} } = {}) {
  const element = document.createElement(tagName);
  if (id) element.id = id;
  if (className) element.className = className;
  if (text) element.textContent = text;
  for (const [name, value] of Object.entries(attrs)) {
    element.setAttribute(name, String(value));
  }
  return element;
}

function createFeedbackOption(value, text) {
  return createFeedbackElement('option', { text, attrs: { value } });
}

function createFeedbackModal() {
  const modal = createFeedbackElement('div', {
    id: 'feedback-modal',
    attrs: { role: 'dialog', 'aria-labelledby': 'feedback-title', 'aria-modal': 'true' },
  });
  const header = createFeedbackElement('div', { id: 'feedback-modal-header' });
  const title = createFeedbackElement('h3', { id: 'feedback-title', text: '🐞 反馈问题' });
  const close = createFeedbackElement('button', {
    id: 'feedback-close',
    text: '×',
    attrs: { type: 'button', 'aria-label': '关闭' },
  });
  header.append(title, close);

  const body = createFeedbackElement('div', { id: 'feedback-modal-body' });
  const typeLabel = createFeedbackElement('label', { text: '反馈类型', attrs: { for: 'feedback-type' } });
  const type = createFeedbackElement('select', { id: 'feedback-type' });
  type.append(
    createFeedbackOption('bug', '缺陷 / Bug'),
    createFeedbackOption('suggestion', '功能建议'),
    createFeedbackOption('other', '其他'),
  );
  const descriptionLabel = createFeedbackElement('label', {
    text: '描述您遇到的问题或建议',
    attrs: { for: 'feedback-description' },
  });
  const description = createFeedbackElement('textarea', {
    id: 'feedback-description',
    attrs: { placeholder: '例如：点击生成按钮后页面卡住了，控制台有报错...' },
  });
  const snapshotControls = createFeedbackElement('div', { id: 'feedback-snapshot-controls' });
  const capture = createFeedbackElement('button', {
    id: 'feedback-capture',
    text: '📷 截取当前页面快照',
    attrs: { type: 'button' },
  });
  const preview = createFeedbackElement('div', { id: 'feedback-snapshot-preview' });
  const image = createFeedbackElement('img', {
    id: 'feedback-snapshot-image',
    attrs: { alt: '待提交的页面快照预览' },
  });
  const snapshotStatus = createFeedbackElement('div', {
    id: 'feedback-snapshot-status',
    attrs: { role: 'status', 'aria-live': 'polite' },
  });
  preview.append(image, snapshotStatus);
  snapshotControls.append(capture, preview);
  const info = createFeedbackElement('div', {
    id: 'feedback-info',
    text: '提交时将自动附带页面地址、浏览器信息及最近 50 条页面操作/报错日志。截图时浏览器会要求选择当前标签页，授权后反馈窗口会暂时隐藏并生成快照。',
  });
  body.append(typeLabel, type, descriptionLabel, description, snapshotControls, info);

  const footer = createFeedbackElement('div', { id: 'feedback-modal-footer' });
  footer.append(
    createFeedbackElement('button', {
      id: 'feedback-cancel',
      className: 'feedback-btn feedback-btn-cancel',
      text: '取消',
      attrs: { type: 'button' },
    }),
    createFeedbackElement('button', {
      id: 'feedback-submit',
      className: 'feedback-btn feedback-btn-submit',
      text: '提交反馈',
      attrs: { type: 'button' },
    }),
  );
  modal.append(header, body, footer);
  return modal;
}

// 遮罩 + 弹窗
const overlay = document.createElement('div');
overlay.id = 'feedback-overlay';
overlay.setAttribute('aria-hidden', 'true');
overlay.append(createFeedbackModal());

document.body.appendChild(trigger);
document.body.appendChild(overlay);

// ==================== 环境收集器 ====================

function collectBrowserInfo() {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform || 'unknown',
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    cookiesEnabled: navigator.cookieEnabled,
    online: navigator.onLine,
  };
}

const STEP_NAMES = {
  1: 'ASIN 抓数据', 2: '构建关键词库', 3: '生成 Listing',
  4: 'AI 优化中心',
};
const STEP_PANELS = {
  1: 'step-input', 2: 'step-keywords', 3: 'step-generation',
  4: 'optimization-center-panel',
};

function detectActiveSection() {
  try {
    for (const [id, panelId] of Object.entries(STEP_PANELS)) {
      const el = document.getElementById(panelId);
      if (el && el.style.display !== 'none' && el.offsetParent !== null) {
        return { stepId: Number(id), stepName: STEP_NAMES[id] || `Step ${id}` };
      }
    }
  } catch { /* ignore */ }
  return { stepId: null, stepName: null };
}

function detectSubSection(stepId) {
  try {
    if (stepId === 3) {
      const sections = [
        { id: 'title-section', name: 'Title' },
        { id: 'bullets-section', name: 'Bullets' },
        { id: 'description-section', name: 'Description' },
        { id: 'search-terms-section', name: 'SearchTerms' },
      ];
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (el && el.offsetParent !== null) return s.name;
      }
    }
    if (stepId === 2) {
      const activeTab = document.querySelector('#keyword-tabs .active, #keyword-tabs [aria-selected="true"]');
      if (activeTab) return activeTab.textContent.trim();
    }
  } catch { /* ignore */ }
  return null;
}

function collectAppState() {
  const state = {};

  const safeKeys = ['listing-lang', 'currentStep', 'restoredListing', 'listing-marketplace'];
  for (const key of safeKeys) {
    try {
      const val = localStorage.getItem(key);
      if (val !== null) state[key] = val;
    } catch { /* ignore */ }
  }

  const { stepId, stepName } = detectActiveSection();
  if (stepId) {
    state.stepId = stepId;
    state.stepName = stepName;
    state.sectionName = detectSubSection(stepId);
  } else {
    const adminView = document.querySelector('.sidebar-nav a.active')?.dataset?.view
      || new URLSearchParams(location.search).get('view');
    if (adminView) state.adminView = adminView;
  }

  if (window.__feedbackErrors && Array.isArray(window.__feedbackErrors)) {
    state.recentErrors = window.__feedbackErrors.slice(-5);
  }

  return state;
}

// 最近 50 条页面操作和错误只保存在内存，提交反馈时才随表单发送。
if (!window.__feedbackActivityLog) window.__feedbackActivityLog = [];
if (!window.__feedbackErrors) window.__feedbackErrors = [];

function appendFeedbackLog(entry) {
  const item = {
    time: new Date().toISOString(),
    type: 'action',
    ...entry,
  };
  window.__feedbackActivityLog.push(item);
  if (window.__feedbackActivityLog.length > 50) window.__feedbackActivityLog.shift();
  if (item.type === 'error') {
    window.__feedbackErrors.push({ time: item.time, message: item.message || item.action || '未知错误' });
    if (window.__feedbackErrors.length > 10) window.__feedbackErrors.shift();
  }
}

function safeLogText(value) {
  try {
    if (value instanceof Error) return `${value.name}: ${value.message}`;
    return typeof value === 'string' ? value : JSON.stringify(value);
  } catch {
    return String(value || '');
  }
}

const FEEDBACK_SECRET_KEY_RE = /(?:authorization|cookie|password|passwd|pwd|token|api[_-]?key|secret|credential|private[_-]?key|session)/i;
const FEEDBACK_SAFE_VALUE_KEY_RE = /^(?:id|[a-z0-9_]*_id|[a-z0-9]*Id|asin|jobId|listingId|marketplace|site|siteCode|language|lang|step|stepId|section|sectionName|status|type|mode|provider|model|page|size|limit|offset|sort|action)$/i;
const FEEDBACK_MAX_SUMMARY_DEPTH = 4;

function summarizeDiagnosticValue(value, key = '', depth = 0) {
  if (FEEDBACK_SECRET_KEY_RE.test(key)) return '[REDACTED]';
  if (value == null || typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (/^data:[^;,]+;base64,/i.test(value)) return `[BINARY_DATA:${value.length}]`;
    if (!FEEDBACK_SAFE_VALUE_KEY_RE.test(key) || value.length > 160) return `[STRING:${value.length}]`;
    return value.slice(0, 160);
  }
  if (depth >= FEEDBACK_MAX_SUMMARY_DEPTH) return '[MAX_DEPTH]';
  if (Array.isArray(value)) {
    return {
      type: 'array',
      length: value.length,
      sample: value.slice(0, 3).map((item) => summarizeDiagnosticValue(item, key, depth + 1)),
    };
  }
  if (typeof value === 'object') {
    const output = {};
    Object.entries(value).slice(0, 30).forEach(([childKey, childValue]) => {
      output[childKey] = summarizeDiagnosticValue(childValue, childKey, depth + 1);
    });
    return output;
  }
  return String(value);
}

function summarizeRequestBody(body) {
  if (body == null) return null;
  try {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const output = {};
      for (const [key, value] of body.entries()) {
        const summarized = typeof File !== 'undefined' && value instanceof File
          ? { type: 'file', size: value.size, mime: value.type || null }
          : summarizeDiagnosticValue(value, key);
        if (Object.hasOwn(output, key)) {
          output[key] = Array.isArray(output[key]) ? [...output[key], summarized] : [output[key], summarized];
        } else {
          output[key] = summarized;
        }
      }
      return output;
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return summarizeDiagnosticValue(Object.fromEntries(body.entries()));
    }
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
      return { type: 'blob', size: body.size, mime: body.type || null };
    }
    if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
      return { type: 'binary', size: body.byteLength };
    }
    if (typeof body === 'string') {
      try {
        return summarizeDiagnosticValue(JSON.parse(body));
      } catch {
        return { type: 'text', length: body.length };
      }
    }
    return summarizeDiagnosticValue(body);
  } catch {
    return { type: typeof body, summary: 'unavailable' };
  }
}

function normalizeDiagnosticUrl(input) {
  try {
    const raw = typeof Request !== 'undefined' && input instanceof Request ? input.url : String(input || '');
    const parsed = new URL(raw, location.origin);
    for (const [key, value] of parsed.searchParams.entries()) {
      const safeValue = FEEDBACK_SECRET_KEY_RE.test(key)
        ? '[REDACTED]'
        : (FEEDBACK_SAFE_VALUE_KEY_RE.test(key) ? String(value).slice(0, 120) : `[VALUE:${String(value).length}]`);
      parsed.searchParams.set(key, safeValue);
    }
    return `${parsed.pathname}${parsed.search}`.slice(0, 500);
  } catch {
    return String(input || '').slice(0, 500);
  }
}

function collectRelatedTraceIds() {
  const values = Array.isArray(window.__feedbackActivityLog) ? window.__feedbackActivityLog : [];
  return [...new Set(values
    .map((entry) => String(entry?.traceId || '').trim())
    .filter((value) => /^[a-zA-Z0-9_-]{8,64}$/.test(value)))]
    .slice(-5);
}

if (!window.__feedbackLoggingInstalled) {
  window.__feedbackLoggingInstalled = true;
  const origError = console.error;
  console.error = function (...args) {
    try {
      const message = args.map(safeLogText).join(' ').slice(0, 1000);
      appendFeedbackLog({ type: 'error', action: 'console.error', message });
    } catch { /* ignore */ }
    origError.apply(console, args);
  };

  window.addEventListener('error', (event) => {
    appendFeedbackLog({
      type: 'error',
      action: 'window.error',
      target: event.filename ? `${event.filename}:${event.lineno || 0}` : null,
      message: String(event.message || '页面脚本错误').slice(0, 1000),
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    appendFeedbackLog({
      type: 'error',
      action: 'unhandledrejection',
      message: safeLogText(event.reason).slice(0, 1000),
    });
  });
  const originalFetch = window.fetch.bind(window);
  window.fetch = async function feedbackDiagnosticFetch(input, init = {}) {
    const startedAt = performance.now();
    const method = String(init?.method || (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET') || 'GET').toUpperCase();
    const url = normalizeDiagnosticUrl(input);
    const requestSummary = summarizeRequestBody(init?.body);
    try {
      const response = await originalFetch(input, init);
      if (!response.ok) {
        appendFeedbackLog({
          type: 'request_error',
          action: 'fetch',
          target: url,
          message: `${method} ${response.status}`,
          method,
          url,
          status: response.status,
          durationMs: Math.round(performance.now() - startedAt),
          traceId: response.headers.get('x-trace-id') || null,
          requestSummary,
        });
      }
      return response;
    } catch (error) {
      appendFeedbackLog({
        type: 'request_error',
        action: 'fetch',
        target: url,
        message: `${method} NETWORK_ERROR · ${safeLogText(error).slice(0, 500)}`,
        method,
        url,
        status: 0,
        durationMs: Math.round(performance.now() - startedAt),
        traceId: null,
        requestSummary,
      });
      throw error;
    }
  };
  document.addEventListener('click', (event) => {
    const target = event.target?.closest?.('button, a, [role="button"], input[type="submit"]');
    if (!target) return;
    const label = target.getAttribute('aria-label')
      || target.getAttribute('title')
      || target.textContent
      || target.id
      || target.tagName;
    appendFeedbackLog({
      type: 'action',
      action: 'click',
      target: `${target.tagName.toLowerCase()}${target.id ? `#${target.id}` : ''}`,
      message: String(label || '').trim().replace(/\s+/g, ' ').slice(0, 200),
    });
  }, true);
  window.addEventListener('hashchange', () => appendFeedbackLog({ type: 'action', action: 'hashchange', target: location.hash }));
  window.addEventListener('popstate', () => appendFeedbackLog({ type: 'action', action: 'navigation', target: location.pathname + location.search }));
  appendFeedbackLog({ type: 'action', action: 'page_ready', target: location.pathname + location.search });
}

let screenshotDataUrl = null;

function resetScreenshot() {
  screenshotDataUrl = null;
  const preview = document.getElementById('feedback-snapshot-preview');
  const image = document.getElementById('feedback-snapshot-image');
  const status = document.getElementById('feedback-snapshot-status');
  const button = document.getElementById('feedback-capture');
  preview?.classList.remove('visible');
  if (image) image.removeAttribute('src');
  if (status) status.textContent = '';
  if (button) button.textContent = '📷 截取当前页面快照';
}

async function captureCurrentPage() {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new Error('当前浏览器不支持页面快照，请升级浏览器后重试');
  }
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: { ideal: 30, max: 30 } },
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: 'include',
    surfaceSwitching: 'exclude',
  });
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;

  const waitForNextCapturedFrame = () => new Promise((resolve) => {
    if (typeof video.requestVideoFrameCallback !== 'function') {
      setTimeout(resolve, 120);
      return;
    }
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve();
    };
    const timeoutId = setTimeout(finish, 800);
    video.requestVideoFrameCallback(finish);
  });

  try {
    await new Promise((resolve, reject) => {
      video.onloadedmetadata = resolve;
      video.onerror = () => reject(new Error('无法读取页面快照'));
    });
    await video.play();
    // Remove the complete feedback layer (modal + dimming + backdrop-filter),
    // then wait for new capture-stream frames. requestAnimationFrame alone only
    // confirms DOM paint and can still leave the old modal frame in the stream.
    overlay.classList.add('is-capturing');
    void document.documentElement.offsetHeight;
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    await waitForNextCapturedFrame();
    await waitForNextCapturedFrame();
    await waitForNextCapturedFrame();
    const sourceWidth = video.videoWidth || window.innerWidth;
    const sourceHeight = video.videoHeight || window.innerHeight;
    const scale = Math.min(1, 1920 / sourceWidth);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(sourceWidth * scale));
    canvas.height = Math.max(1, Math.round(sourceHeight * scale));
    const context = canvas.getContext('2d', { alpha: false });
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    screenshotDataUrl = canvas.toDataURL('image/jpeg', 0.82);
    appendFeedbackLog({ type: 'action', action: 'feedback_snapshot_captured', target: `${canvas.width}x${canvas.height}` });
    return { width: canvas.width, height: canvas.height };
  } finally {
    overlay.classList.remove('is-capturing');
    stream.getTracks().forEach((track) => track.stop());
    video.srcObject = null;
  }
}

// ==================== 交互逻辑 ====================
const feedbackModal = document.getElementById('feedback-modal');
const feedbackFocusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');
let previousFeedbackFocus = null;
let feedbackCloseTimer = null;

function getFeedbackFocusable() {
  return Array.from(feedbackModal.querySelectorAll(feedbackFocusableSelector))
    .filter((element) => element.getClientRects().length > 0);
}

function openModal() {
  previousFeedbackFocus = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : trigger;
  clearTimeout(feedbackCloseTimer);
  overlay.setAttribute('aria-hidden', 'false');
  overlay.classList.add('open');
  requestAnimationFrame(() => {
    requestAnimationFrame(() => overlay.classList.add('visible'));
  });
  document.getElementById('feedback-description').focus();
}

function closeModal() {
  if (!overlay.classList.contains('open')) return;
  overlay.classList.remove('visible');
  const returnFocus = previousFeedbackFocus?.isConnected ? previousFeedbackFocus : trigger;
  clearTimeout(feedbackCloseTimer);
  feedbackCloseTimer = setTimeout(() => {
    overlay.classList.remove('open');
    overlay.setAttribute('aria-hidden', 'true');
    document.getElementById('feedback-description').value = '';
    document.getElementById('feedback-submit').disabled = false;
    document.getElementById('feedback-submit').textContent = '提交反馈';
    resetScreenshot();
    returnFocus.focus({ preventScroll: true });
    previousFeedbackFocus = null;
  }, 250);
}

// 点击悬浮按钮
trigger.addEventListener('click', openModal);

document.getElementById('feedback-capture').addEventListener('click', async () => {
  const button = document.getElementById('feedback-capture');
  const preview = document.getElementById('feedback-snapshot-preview');
  const image = document.getElementById('feedback-snapshot-image');
  const status = document.getElementById('feedback-snapshot-status');
  button.disabled = true;
  button.textContent = '正在获取快照...';
  try {
    const dimensions = await captureCurrentPage();
    image.src = screenshotDataUrl;
    preview.classList.add('visible');
    status.textContent = `已截取 ${dimensions.width} × ${dimensions.height}，将作为附件提交。`;
    button.textContent = '📷 重新截取页面快照';
  } catch (error) {
    const cancelled = error?.name === 'NotAllowedError' || error?.name === 'AbortError';
    showToast(cancelled ? '已取消页面快照' : (error.message || '页面快照失败'), { type: cancelled ? 'info' : 'error', id: 'feedback-toast' });
    button.textContent = screenshotDataUrl ? '📷 重新截取页面快照' : '📷 截取当前页面快照';
  } finally {
    button.disabled = false;
  }
});

// 关闭弹窗
document.getElementById('feedback-close').addEventListener('click', closeModal);
document.getElementById('feedback-cancel').addEventListener('click', closeModal);
overlay.addEventListener('click', (e) => {
  if (e.target === overlay) closeModal();
});

// ESC 关闭
document.addEventListener('keydown', (e) => {
  if (!overlay.classList.contains('open')) return;
  if (e.key === 'Escape') {
    e.preventDefault();
    closeModal();
    return;
  }
  if (e.key !== 'Tab') return;
  const focusable = getFeedbackFocusable();
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable.at(-1);
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
});

// 提交反馈
document.getElementById('feedback-submit').addEventListener('click', async () => {
  const description = document.getElementById('feedback-description').value.trim();
  if (!description) {
    showToast('请填写反馈描述', { type: 'error', id: 'feedback-toast' });
    return;
  }

  const submitBtn = document.getElementById('feedback-submit');
  submitBtn.disabled = true;
  submitBtn.textContent = '提交中...';

  const payload = {
    description,
    feedbackType: document.getElementById('feedback-type')?.value || 'bug',
    pageUrl: window.location.href,
    browserInfo: collectBrowserInfo(),
    appState: collectAppState(),
    clientLogs: Array.isArray(window.__feedbackActivityLog) ? window.__feedbackActivityLog.slice(-50) : [],
    relatedTraceIds: collectRelatedTraceIds(),
    screenshotDataUrl,
  };

  try {
    const response = await fetch('/api/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || '提交失败');
    }

    showToast('反馈已提交，感谢您的反馈！', { type: 'success', id: 'feedback-toast' });
    closeModal();
  } catch (error) {
    console.error('[Feedback] 提交失败:', error);
    showToast(error.message || '提交失败，请稍后重试', { type: 'error', id: 'feedback-toast' });
    submitBtn.disabled = false;
    submitBtn.textContent = '提交反馈';
  }
});
